import React from 'react';

// Base button props interface
interface BaseButtonProps {
  children?: React.ReactNode;
  onClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  id?: string;
  testId?: string;
  className?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  size?: 'xs' | 'sm' | 'base' | 'l' | 'xl' | 'deleteButton';
}

// Base button component with shared styling
const BaseButton: React.FC<BaseButtonProps & { variantClasses: string }> = ({
  children,
  onClick,
  disabled = false,
  type = 'button',
  id,
  testId,
  className = '',
  icon,
  iconPosition = 'left',
  size = 'base',
  variantClasses
}) => {
  // Size-specific classes
  const sizeClasses = {
    xs: 'px-[11px] py-[7px] text-[12px] leading-[16px]',
    sm: 'px-[13px] py-[9px] text-[14px] leading-[16px]',
    base: 'px-[17px] py-[9px] text-[14px] leading-[20px]',
    l: 'px-[17px] py-[9px] text-[16px] leading-[24px]',
    xl: 'px-[25px] py-[13px] text-[16px] leading-[24px]',
    deleteButton: 'px-[17px] py-[7px] text-xs leading-[16px]'
  };

  const baseClasses =
    'inline-flex items-center justify-center rounded-[100px] font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2';

  const renderContent = () => {
    if (!children && icon) {
      // Icon only
      return icon;
    }

    if (children && !icon) {
      // Text only
      return children;
    }

    if (children && icon) {
      // Text and icon
      return iconPosition === 'left' ? (
        <>
          <span className="mr-2">{icon}</span>
          {children}
        </>
      ) : (
        <>
          {children}
          <span className="ml-2">{icon}</span>
        </>
      );
    }

    return children;
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      id={id}
      data-testid={testId}
      className={`${baseClasses} ${sizeClasses[size]} ${variantClasses} ${className}`}
    >
      {renderContent()}
    </button>
  );
};

// Primary Button Component
export const PrimaryButton: React.FC<BaseButtonProps> = (props) => {
  const variantClasses =
    'bg-cyan-300 text-cyan-950 hover:bg-cyan-500 focus:bg-cyan-300 focus:ring-cyan-500 active:ring-cyan-500 disabled:bg-gray-100 disabled:text-gray-200 disabled:shadow-sm disabled:cursor-not-allowed';
  return <BaseButton {...props} variantClasses={variantClasses} />;
};

// Secondary Button Component
export const SecondaryButton: React.FC<BaseButtonProps> = (props) => {
  const variantClasses =
    'bg-rose-500 text-white hover:bg-rose-600 focus:bg-rose-500 focus:ring-rose-600 active:ring-rose-600 disabled:bg-gray-100 disabled:text-gray-200 disabled:shadow-sm disabled:cursor-not-allowed';
  return <BaseButton {...props} variantClasses={variantClasses} />;
};

// White Button Component
export const WhiteButton: React.FC<BaseButtonProps> = (props) => {
  const variantClasses =
    'bg-white text-gray-700 border border-gray-300 hover:bg-gray-250 focus:bg-white focus:ring-gray-550 active:ring-gray-550 disabled:bg-white disabled:text-gray-300 disabled:border-gray-100 disabled:cursor-not-allowed';
  return <BaseButton {...props} variantClasses={variantClasses} />;
};

// Text Button Component
export const TextButton: React.FC<BaseButtonProps> = (props) => {
  const variantClasses =
    'ring-inset bg-transparent text-purple-800 border border-transparent hover:text-purple-900 focus:bg-transparent focus:border-purple-700 active:border-purple-700 disabled:text-gray-200 disabled:bg-transparent disabled:border-transparent disabled:cursor-not-allowed';
  return <BaseButton {...props} variantClasses={variantClasses} />;
};

// Text Secondary Button Component
export const TextSecondaryButton: React.FC<BaseButtonProps> = (props) => {
  const variantClasses =
    'bg-transparent text-rose-800 border border-transparent hover:text-rose-900 focus:bg-transparent focus:border-orange-700 focus:ring-rose-800 active:border-orange-700 disabled:text-gray-300 disabled:bg-transparent disabled:border-transparent disabled:cursor-not-allowed';
  return <BaseButton {...props} variantClasses={variantClasses} />;
};

// Export all button types for convenience
export type { BaseButtonProps };
